/**
 * src/pages/admin/AdminFeedback.js
 * 
 * Admin Feedback Collection & Review page.
 * View user feedback, filter by status/category, mark as reviewed.
 */

import React, { useEffect, useState } from "react";
import {
  MessageSquareText,
  Star,
  CheckCircle2,
  Clock,
  Eye,
  Filter,
  Inbox,
  User,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAdmin } from "../../hooks/useAdmin";

const STATUS_OPTIONS = [
  { id: null, label: "All" },
  { id: "new", label: "New" },
  { id: "helpful", label: "Helpful" },
  { id: "not_helpful", label: "Not Helpful" },
  { id: "reviewed", label: "Reviewed" },
];

const CATEGORY_COLORS = {
  general: "bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-400",
  bug: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  feature: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  complaint: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  praise: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  suggestion: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

function formatTime(ts) {
  if (!ts) return "";
  const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
}

function RatingStars({ rating }) {
  if (rating == null) return null;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-3 h-3 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-neutral-600'}`}
        />
      ))}
    </div>
  );
}

function FeedbackCard({ feedback, onUpdateStatus, adminUser }) {
  const toast = useToast();
  const [adminNote, setAdminNote] = useState(feedback.adminNote || "");
  const [showActions, setShowActions] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    loading: false,
    action: null,
  });

  const category = feedback.category || "general";
  const isNew = feedback.status === "new";

  const handleUpdate = async (status) => {
    setConfirmDialog({
      open: true,
      title: "Update Feedback Status",
      message: `Mark this feedback as ${status.replace("_", " ")}?`,
      loading: false,
      action: async () => {
        await adminService.withAdminRetry(() => adminService.updateFeedbackStatus(adminUser, feedback.id, status, adminNote));
        toast.success(`Feedback marked as ${status}`);
        setShowActions(false);
        onUpdateStatus?.();
      },
    });
  };

  const runConfirmAction = async () => {
    if (!confirmDialog.action) return;
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      await confirmDialog.action();
      setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(`Failed: ${err?.message || "Action failed"}`);
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl border ${isNew ? 'border-green-300 dark:border-green-800' : 'border-gray-200 dark:border-neutral-700'} p-4`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 text-sm font-bold text-green-700 dark:text-green-400">
          {(feedback.userEmail || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {feedback.userEmail || "Unknown User"}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${CATEGORY_COLORS[category] || CATEGORY_COLORS.general}`}>
              {category}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              isNew ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-500 dark:bg-neutral-700 dark:text-gray-400'
            }`}>
              {feedback.status || "new"}
            </span>
          </div>

          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 whitespace-pre-wrap">
            {feedback.message}
          </p>

          {feedback.topic && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Topic: {feedback.topic}
            </p>
          )}

          {feedback.ipAddress && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              IP: {feedback.ipAddress}
            </p>
          )}

          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <RatingStars rating={feedback.rating} />
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTime(feedback.createdAt)}
            </span>
            {feedback.adminNotificationId && (
              <span className="flex items-center gap-1">
                <MessageSquareText className="w-3 h-3" />
                Response to notification
              </span>
            )}
          </div>

          {/* Admin Note & Actions */}
          {showActions && (
            <div className="mt-3 space-y-2">
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add admin note (optional)…"
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleUpdate("helpful")}
                  className="px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-200 transition"
                >
                  Helpful
                </button>
                <button
                  onClick={() => handleUpdate("not_helpful")}
                  className="px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-200 transition"
                >
                  Not Helpful
                </button>
                <button
                  onClick={() => handleUpdate("reviewed")}
                  className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400 text-xs font-medium hover:bg-gray-200 transition"
                >
                  Mark Reviewed
                </button>
              </div>
            </div>
          )}

          {!showActions && isNew && (
            <button
              onClick={() => setShowActions(true)}
              className="mt-2 text-xs font-medium text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
            >
              <Eye className="w-3 h-3" />
              Review
            </button>
          )}

          {feedback.adminNote && !showActions && (
            <div className="mt-2 p-2 rounded bg-gray-50 dark:bg-neutral-900 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">Admin note:</span> {feedback.adminNote}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        confirmLabel="Confirm"
        onConfirm={runConfirmAction}
        onCancel={() => {
          if (confirmDialog.loading) return;
          setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}

export default function AdminFeedback() {
  const { user: adminUser } = useAdmin();
  const [feedback, setFeedback] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    const unsub = adminService.listAdminFeedback((data) => {
      setFeedback(data);
    }, statusFilter);
    return () => unsub();
  }, [statusFilter]);

  const counts = {};
  feedback.forEach((f) => {
    const s = f.status || "new";
    counts[s] = (counts[s] || 0) + 1;
  });

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <MessageSquareText className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              User Feedback
            </h1>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {feedback.length} total
            </span>
          </div>

          {/* Filters */}
          <div className="flex gap-1.5 overflow-x-auto pb-3">
            {STATUS_OPTIONS.map((f) => (
              <button
                key={f.id || "all"}
                onClick={() => setStatusFilter(f.id)}
                className={`
                  px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap
                  ${statusFilter === f.id
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                  }
                `}
              >
                {f.label}
                {f.id && counts[f.id] ? ` (${counts[f.id]})` : ""}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-3">
        {feedback.length === 0 ? (
          <div className="text-center py-16">
            <Inbox className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No feedback found
            </p>
          </div>
        ) : (
          feedback.map((item) => (
            <FeedbackCard key={item.id} feedback={item} adminUser={adminUser} />
          ))
        )}
      </div>
    </div>
  );
}
